import React, { CSSProperties, ReactNode } from 'react';
import cn from 'classnames';
import styles from './styledContainer.module.scss';
import { Spacing } from '../Grid/interfaces';
import { ResponsiveImage } from '../../ResponsiveImage/ResponsiveImage';

type TextAlignment = 'center' | 'left' | 'right' | 'justify';

export interface StyledContainerProps {
    children?: ReactNode;
    backgroundColor?: string;
    backgroundColorMobile?: string;
    avoidBackgroundColorFallback?: boolean;
    textColor?: string;
    textAlign?: TextAlignment;
    paddingTop?: Spacing;
    paddingBottom?: Spacing;
    backgroundImageMobile?: string;
    backgroundImageTablet?: string;
    backgroundImageDesktop?: string;
    backgroundGradientMobile?: string;
    backgroundGradientDesktop?: string;
    backgroundGradientTablet?: string;
    includeNavPadding?: boolean;
    includeNoHeroPadding?: boolean;
    standardMoleculeSpacing?: boolean;
    tag?: 'div' | 'section';
    className?: string;
    id?: string;
    backgroundImageEffect?: boolean;
    style?: CSSProperties;
    dataComponentType?: string;
    eagerHero?: boolean;
}

const stripUrl = (val?: string) => {
    if (!val) return undefined;
    const raw = val
        .replace(/^url\((.*)\)$/i, '$1')
        .replace(/^['"]|['"]$/g, '')
        .trim();
    if (!raw || /^(undefined|null|false)$/i.test(raw)) return undefined;
    return raw;
};

export function StyledContainer(props: StyledContainerProps): React.JSX.Element {
    const Tag = props.tag || 'div';

    const mainStylings = props.backgroundImageEffect ? styles.backgroundImageEffect : styles.styledContainer;

    const mobile = stripUrl(props.backgroundImageMobile);
    const tablet = stripUrl(props.backgroundImageTablet);
    const desktop = stripUrl(props.backgroundImageDesktop);

    const loading = props.eagerHero ? 'eager' : 'lazy';
    const fetchPriority = props.eagerHero ? 'high' : 'auto';

    const hasAnyUrl = [mobile, tablet, desktop].some((src) => typeof src === 'string' && src.length > 0);

    const classNames = cn(mainStylings, props.className, {
        [styles[`text-${props.textAlign?.toLowerCase()}`]]: props.textAlign,
        [styles.navPadding]: props.includeNavPadding,
        [styles.noHero]: props.includeNoHeroPadding,
        'standard-molecule-spacing': props.standardMoleculeSpacing,
        [styles.backgroundColorMobile]:
            props.backgroundColorMobile || (!props.avoidBackgroundColorFallback && props.backgroundColor),
        [styles.backgroundColorDesktop]: props.backgroundColor,
        [styles.backgroundGradient]:
            props.backgroundGradientDesktop || props.backgroundGradientTablet || props.backgroundGradientMobile,
        [styles.backgroundImage]: hasAnyUrl,
    });

    const style: React.CSSProperties = {
        ...(!!props.backgroundColor && { '--bg-color-desktop': props.backgroundColor }),
        ...(!!props.backgroundColorMobile && { '--bg-color-mobile': props.backgroundColorMobile }),
        ...(!props.avoidBackgroundColorFallback &&
            !props.backgroundColorMobile && { '--bg-color-mobile': props.backgroundColor }),
        ...(props.textColor && { color: props.textColor }),
        ...(props.paddingTop && { paddingTop: Number(props.paddingTop) }),
        ...(props.paddingBottom && { paddingBottom: props.paddingBottom }),
        ...(!props.eagerHero && props.backgroundImageDesktop && { '--bg-desktop': props.backgroundImageDesktop }),
        ...(!props.eagerHero && props.backgroundImageTablet && { '--bg-tablet': props.backgroundImageTablet }),
        ...(!props.eagerHero && props.backgroundImageMobile && { '--bg-mobile': props.backgroundImageMobile }),
        ...(props.backgroundGradientDesktop && { '--bg-gradient-desktop': `${props.backgroundGradientDesktop}` }),
        ...(props.backgroundGradientTablet && { '--bg-gradient-tablet': `${props.backgroundGradientTablet}` }),
        ...(props.backgroundGradientMobile && { '--bg-gradient-mobile': `${props.backgroundGradientMobile}` }),
        ...props.style,
    };

    return (
        <Tag className={classNames} style={style} id={props.id} data-componenttype={props?.dataComponentType}>
            {props.eagerHero && hasAnyUrl ? (
                <ResponsiveImage
                    aria-hidden={true}
                    loading={loading}
                    fetchPriority={fetchPriority}
                    mobile={mobile ? { source: mobile, altText: '' } : undefined}
                    tablet={tablet ? { source: tablet, altText: '' } : undefined}
                    desktop={desktop ? { source: desktop, altText: '' } : undefined}
                    className={styles.responsiveImage}
                />
            ) : null}
            {props.children}
        </Tag>
    );
}
